// The chat model vocabulary, in one place.
//
// Two surfaces choose a chat model and they must say the same words: the
// command bar's model pill (a listbox the user opens between messages) and the
// modals that fire a turn on the user's behalf (Merge, Change template). Before
// this module the pill hardcoded its two Claude entries and the modals had no
// picker at all, so there was nothing to drift from — now there is.
//
// Deliberately NOT merged with `CLAUDE_MODELS` in `routes/new-deck/shared.jsx`.
// That list steers a whole headless deck BUILD and its hints are written for
// that decision ("Worth it mainly for very large or complex decks"); these hints
// are written for a single chat turn. Same two model ids, different advice.

/**
 * The Claude models a chat turn can run on. Hints are the pill's, verbatim.
 *
 * Opus first, and Sonnet is OFF unless the ⚙ "Offer Claude Sonnet" toggle is on
 * (2026-08-01, Jake): on the subscription Sonnet is barely cheaper, no quicker
 * in practice, and its edits were measurably worse — so a two-model pill was
 * offering a choice with a wrong answer. The entry stays here rather than being
 * deleted so flipping the toggle brings it back whole.
 */
/** One row of a model picker. `detail` names the metered model behind the
 *  OpenCode option; the Claude entries have no second line. */
export type ChatModelOption = {
  value: string;
  label: string;
  hint: string;
  detail?: string;
  /** Charged at API rates. THE field every surface keys on for billing — the
   *  pill's divider and chip, the merge bar. Nothing may re-derive this from an
   *  engine NAME again: "OpenCode costs money, Claude doesn't" was true
   *  right up until Claude gained a metered mode. */
  metered?: boolean;
};

export const CHAT_MODELS: ChatModelOption[] = [
  {
    value: 'opus',
    label: 'Claude Opus',
    hint: 'The default. Edits slides directly on the subscription and can source stock photos/video.',
  },
  {
    value: 'sonnet',
    label: 'Claude Sonnet',
    hint: 'Cheaper per token, but no faster on a subscription turn and weaker on edits. Off unless the ⚙ drawer offers it.',
  },
];

/** The optional metered model. One value, because the server pre-picks WHICH
 *  metered model from env keys — the user only chooses whether to use it. */
export const FAST_VALUE = 'fast';

/** The optional Pi agent. One value again — the ⚙ drawer picks the endpoint,
 *  the model and the rung; the pill only chooses whether to use it. */
export const PI_VALUE = 'pi';
export const CODEX_VALUE = 'codex';

/**
 * A RUNG TABLE AS THE SERVER SENDS IT: model id → the rungs that model takes.
 *
 * Three states, and collapsing any two of them is the bug these helpers exist
 * to prevent: an ARRAY (offer those), an EMPTY array (it reasons, with no
 * settable rung) and an ABSENT key (an id we have never heard of — offer
 * everything and let the endpoint be the judge, because a model newer than our
 * roster is likelier than a typo). A missing MAP is the absent case for every
 * id at once, not a reason to disable a dial.
 */
export type RungTable = Record<string, string[] | undefined> | null | undefined;

/** Every rung the OpenCode Go gateway's validator accepts from anyone — the
 *  union across its whole roster, not any one model's ladder. Offered only when
 *  we do not know the picked model. */
export const GATEWAY_EFFORT_UNION = ['none', 'minimal', 'low', 'medium', 'high', 'xhigh', 'max'];

/** Pi's `--thinking` ladder: the HARNESS's ceiling, mirroring
 *  `PI_EFFORT_LEVELS` on the server. Pi maps whichever of these it is given
 *  onto the endpoint's own reasoning parameter, so it is the widest a Pi dial
 *  can be, never a promise that the model in the slot has all seven. */
export const PI_EFFORT_LADDER = ['off', 'minimal', 'low', 'medium', 'high', 'xhigh', 'max'];

/** What a dial may offer, and whether the answer is knowledge or ignorance.
 *  `known: false` means the table has never heard of this model — the caller
 *  must not then "correct" a stored rung, because absence is not evidence. */
export type RungOffer = { efforts: string[]; known: boolean };

/** This model's row, or null when the table has never heard of it. */
function rowFor(table: RungTable, model: string | null | undefined): string[] | null {
  if (!model || !table || !Object.prototype.hasOwnProperty.call(table, model)) return null;
  return table[model] || [];
}

/**
 * A gateway (OpenCode) model's dial. Rungs listed → those plus `none`, which is an
 * off switch every model with a dial honours and models.dev does not list;
 * `[]` → no dial at all, so show no row rather than buttons that do nothing;
 * unknown → the union, so our ignorance never blocks the author.
 *
 * Mirrors `gatewayLadder()` in server/llm/chat-efforts.ts, which is what the
 * chat pill's own list comes from.
 */
export function gatewayRungs(table: RungTable, model: string | null | undefined): RungOffer {
  const rungs = rowFor(table, model);
  if (rungs === null) return { efforts: GATEWAY_EFFORT_UNION, known: false };
  if (rungs.length === 0) return { efforts: [], known: true };
  return { efforts: ['none', ...rungs.filter((r) => r !== 'none')], known: true };
}

/**
 * A Pi model's dial — the same table, projected onto Pi's ladder instead of the
 * gateway's. Mirrors `piEffortLadder()` in server/llm/settings.ts, which is
 * what the PUT accepts and what the chat pill is offered.
 *
 * Two things make it differ from `gatewayRungs`:
 *   - `off` is PI'S OWN rung (pass no `--thinking`) rather than the provider's,
 *     so it survives on every model, including the ones with no dial at all —
 *     an empty row here is `['off']`, not `[]`.
 *   - the result is filtered through Pi's ladder, so a provider rung Pi has no
 *     flag for cannot be offered.
 */
export function piRungs(table: RungTable, model: string | null | undefined): RungOffer {
  const rungs = rowFor(table, model);
  if (rungs === null) return { efforts: PI_EFFORT_LADDER, known: false };
  const takes = new Set(rungs.map((r) => (r === 'none' ? 'off' : r)));
  return { efforts: PI_EFFORT_LADDER.filter((r) => r === 'off' || takes.has(r)), known: true };
}

/**
 * The model the STRUCTURAL verbs start on: merge and split.
 *
 * Every other studio surface starts from the ⚙ "Default model" setting. These
 * two don't, and the reason is the blast radius rather than the difficulty: a
 * merge issues N deletes and a split issues a create, so a model that drops a
 * bullet on the way through doesn't produce an ugly slide, it produces a deck
 * that is missing the user's words. Opus is the safe starting point; the merge
 * bar lets the user move off it, and a two-short-slides merge is fine on Sonnet.
 */
export const STRUCTURAL_MODEL = 'opus';

/**
 * The options a model picker should render, in display order.
 *
 * Every option carries `metered`, and that is the field every surface keys on —
 * the pill's divider, its chip, the merge bar. Nothing should re-derive billing
 * from an engine NAME again: it was true that OpenCode meant "costs money"
 * and Claude did not, right up until Claude gained a metered mode.
 *
 * `claudeAvailable` is the server having the CLI *and* a credential; `fastInfo`
 * is `{ provider, model }` or null; `offerSonnet` is the ⚙ toggle (off by
 * default, so Claude means Opus alone). All come from `useModelChoice`. An
 * empty array means there is nothing to choose — render no picker at all rather
 * than a row with one dead chip. One Claude entry is NOT nothing to choose: the
 * pill still names which model the turn will run on.
 */
/** The server pre-picks WHICH metered model from its env keys; the user only
 *  chooses whether to use it. */
export type FastInfo = { provider: string; model?: string | null };

/** The ChatGPT-plan agent. Same shape as `FastInfo` so the pill treats them
 *  alike; `auth` is what decides whether the chip reads as metered. */

/** The Pi agent. The omission of a per-turn auth flag is
 *  the point: Pi has ONE legal mode, so there is no credential question to ask
 *  and the option's `metered` flag is a constant. */
export type PiInfo = {
  model?: string | null;
  effort?: string | null;
  /** Whose credential a Pi turn spends: 'api' (its own metered key, the
   *  default) or 'subscription' (a `pi /login` on the server). Pi is the one
   *  engine whose auth mode ALSO says which provider is running, which is why
   *  the label below names it. */
  auth?: string | null;
  /** Which login, when `auth` is 'subscription' — the provider id as Pi's own
   *  CLI names it (`openai-codex`, `anthropic`, …). */
  subscriptionProvider?: string | null;
};

/** Whose credential a Claude turn spends. A server setting, not a picker
 *  choice — but the pill has to say when a message will be charged. */
export type ClaudeAuth = 'subscription' | 'api';

export type ModelAvailability = {
  claudeAvailable?: boolean;
  claudeAuth?: ClaudeAuth | string | null;
  fastInfo?: FastInfo | null;
  piInfo?: PiInfo | null;
  codexInfo?: { model?: string | null } | null;
  offerSonnet?: boolean;
};

export function chatModelOptions({
  claudeAvailable,
  claudeAuth,
  fastInfo,
  piInfo,
  codexInfo,
  offerSonnet,
}: ModelAvailability = {}): ChatModelOption[] {
  const claudeMetered = claudeAuth === 'api';
  const opts: ChatModelOption[] = claudeAvailable
    ? CHAT_MODELS.filter((m) => m.value !== 'sonnet' || offerSonnet).map((m) => ({
        ...m,
        metered: claudeMetered,
        hint: claudeMetered ? `${m.hint} Charged to the Anthropic API key, at API rates.` : m.hint,
      }))
    : [];

  if (fastInfo) {
    const name = fastInfo.model || fastInfo.provider;
    opts.push({
      value: FAST_VALUE,
      label: 'OpenCode',
      detail: name,
      metered: true, // OpenCode has one legal mode: it is always a metered key.
      hint: `${name}: metered, near-instant replies with preview cards. Best for quick edits: titles, copy tweaks, small layout nudges.`,
    });
  }
  if (codexInfo)
    opts.push({
      value: CODEX_VALUE,
      label: 'Codex',
      detail: codexInfo.model || 'GPT',
      metered: false,
      hint: 'Edits slides using the server’s ChatGPT subscription, with preview and keep/discard cards.',
    });
  if (piInfo) {
    // NAMED AFTER THE CONFIGURED MODEL, never after a vendor. Pi is a harness
    // pointed at whatever endpoint the ⚙ drawer names, so a label saying
    // "OpenCode" or "DeepSeek" would be wrong the first time someone repointed
    // it — which is the entire reason this engine exists.
    const name = piInfo.model || 'no model chosen';
    // A LOGIN IS NOT METERED BY THIS STUDIO. Pi ran only on its own API key
    // until subscription logins landed (2026-08-10), so this used to be a
    // constant `true`; a pill that still said "metered" on a plan turn would be
    // marking money where none is spent, and the money mark only works if it is
    // exact. What the plan itself costs is between the user and the provider.
    const metered = piInfo.auth !== 'subscription';
    const provider = piInfo.subscriptionProvider || 'the subscription';
    opts.push({
      value: PI_VALUE,
      label: 'Pi',
      detail: name,
      metered,
      hint: metered
        ? `${name}: metered, and edits slides the same way Claude does. It runs on whichever provider Pi is pointed at in the ⚙ drawer, so it is the one to reach for when comparing models.`
        : `${name} on the ${provider} login signed in on this server, and edits slides the same way Claude does. Nothing is charged to the Pi key.`,
    });
  }
  return opts;
}

/**
 * Which SERVER engine a pill value runs on — `chat.engines` in the settings
 * payload is keyed by engine, the pill by model choice, and Claude is the one
 * engine with two entries. The one place that knows the mapping.
 */
export function engineIdFor(choice: string | null | undefined): ChatEngineId {
  if (choice === FAST_VALUE) return 'fast';
  if (choice === CODEX_VALUE) return 'codex';
  if (choice === PI_VALUE) return 'pi';
  return 'claude';
}

/** The engines a chat turn can run on, as `chat.engines` keys them. */
export type ChatEngineId = 'claude' | 'fast' | 'pi' | 'codex';

/**
 * Pick a starting value that actually exists in `options`.
 *
 * `preferred` is a wish, not a guarantee: a server with no Claude credential
 * offers OpenCode alone, so a merge asking for Opus must land on OpenCode rather than
 * sending a `claudeModel` the picker never showed. Returns null when there is
 * nothing to pick.
 */
export function resolveModelChoice(
  options: ChatModelOption[] | null | undefined,
  preferred: string | null | undefined,
): string | null {
  const first = options?.[0];
  if (!first) return null;
  return preferred && options.some((o) => o.value === preferred) ? preferred : first.value;
}

/**
 * Encode a choice as the chat turn's send options.
 *
 * UNCHANGED BY THE AUTH MODES, deliberately: the wire still carries exactly one
 * of `fast` / `engine` / `claudeModel`, and never which credential to spend.
 * That is a server-side property of the engine, and a stale tab able to send a
 * mode would be a way to spend money the settings say not to.
 *
 * The wire carries exactly ONE of `fast`, `engine` or `claudeModel` — see the body
 * assembly in `lib/chatTurns.js` and the `wantFast` branch in
 * `server/routes/chat.ts`. This is the one place that knows that.
 *
 * `effort` is the pill's per-message reasoning rung and rides alongside, never
 * instead of, the engine. It is OPTIONAL on purpose: the studio-fired turns
 * (Merge, Split, Change template) pass a `choice` and no rung, so they run at
 * the ⚙ default. Someone who dialled OpenCode down to answer a quick question must
 * not thereby get a shallower merge — those turns issue N deletes and a
 * rewrite, which is the same reason they stopped inheriting the pill's engine.
 */
/** `effort` is the pill's per-message rung, sent alongside whichever engine the
 *  choice selects — it applies to all three, so it rides on the union rather
 *  than on any one branch. */
export type SendOpts = { effort?: string } & (
  | { fast: true; engine?: never; claudeModel?: never }
  | { engine: typeof PI_VALUE | typeof CODEX_VALUE; codexModel?: string; fast?: never; claudeModel?: never }
  | { claudeModel: string | null; fast?: never; engine?: never }
);

export function sendOptsFor(
  choice: string | null | undefined,
  effort?: string | null,
  codexModel?: string | null,
): SendOpts {
  const rung = effort ? { effort } : {};
  if (choice === FAST_VALUE) return { fast: true, ...rung };
  // Pi rides its own field — one wire
  // shape for "an agent engine that is not the default", so a new one costs a
  // value rather than another mutually-exclusive flag beside `fast`.
  if (choice === CODEX_VALUE) return { engine: choice, ...(codexModel ? { codexModel } : {}), ...rung };
  if (choice === PI_VALUE) return { engine: choice, ...rung };
  return { claudeModel: choice ?? null, ...rung };
}
